import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Model, ModelMode } from '@ytclip/shared'
import { Brain, Mic, RefreshCw, Volume2, Image as ImageIcon, Sparkles } from 'lucide-react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardBody } from '@/components/ui/card.tsx'
import { Badge, EmptyState, ErrorState, Skeleton } from '@/components/ui/feedback.tsx'
import { PageHeader } from '@/components/ui/misc.tsx'
import { refreshModels } from '@/features/admin/api.ts'
import { useModels } from '@/features/shared-hooks.ts'
import { useCurrentUser } from '@/features/user/hooks.ts'

const MODE_ICONS: Partial<Record<ModelMode, ReactNode>> = {
  chat: <Brain className="size-5" />,
  completion: <Brain className="size-5" />,
  audio_transcription: <Mic className="size-5" />,
  audio_speech: <Volume2 className="size-5" />,
  image_generation: <ImageIcon className="size-5" />,
}

const MODE_LABELS: Partial<Record<ModelMode, string>> = {
  chat: 'Chat',
  completion: 'Completion',
  embedding: 'Embedding',
  audio_transcription: 'Transkripsi',
  audio_speech: 'Text to speech',
  image_generation: 'Gambar',
  rerank: 'Rerank',
  moderation: 'Moderasi',
  unknown: 'Lainnya',
}

const CAPABILITY_LABELS: Record<string, string> = {
  vision: 'Vision',
  functionCalling: 'Function calling',
  reasoning: 'Reasoning',
  promptCaching: 'Prompt caching',
  streaming: 'Streaming',
  audioInput: 'Audio input',
  audioOutput: 'Audio output',
  pdfInput: 'PDF',
}

/** Per-token prices are tiny; show them per million tokens, as providers do. */
function pricePerMillion(costPerToken: number | null): string | null {
  if (costPerToken === null || costPerToken <= 0) return null

  return `$${(costPerToken * 1_000_000).toFixed(2)}/1M`
}

export function ModelsPage() {
  const { data, isPending, isError } = useModels()
  const { data: profile } = useCurrentUser()

  return (
    <>
      <PageHeader
        title="Models"
        description="Model yang tersedia beserta harga dan kemampuannya"
        action={profile?.isSuperAdmin ? <RefreshButton /> : undefined}
      />

      {isPending ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-44 w-full rounded-lg" />
          <Skeleton className="h-44 w-full rounded-lg" />
          <Skeleton className="h-44 w-full rounded-lg" />
          <Skeleton className="h-44 w-full rounded-lg" />
        </div>
      ) : isError ? (
        <ErrorState message="Daftar model tidak bisa dimuat." />
      ) : data.length === 0 ? (
        <Card>
          <EmptyState icon={<Sparkles />} title="Belum ada model tersedia" />
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {data.map((model) => (
            <ModelCard key={model.id} model={model} />
          ))}
        </div>
      )}
    </>
  )
}

function ModelCard({ model }: { model: Model }) {
  const capabilities = Object.entries(model.capabilities).filter(([, enabled]) => enabled)
  const input = pricePerMillion(model.inputCostPerToken)
  const output = pricePerMillion(model.outputCostPerToken)

  return (
    <Card interactive>
      <CardBody className="space-y-4">
        <div className="flex items-start gap-3">
          <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-sm bg-primary-container text-on-primary-container">
            {MODE_ICONS[model.mode] ?? <Sparkles className="size-5" />}
          </span>
          <div className="min-w-0">
            <p className="truncate text-title-lg font-medium text-on-surface">{model.name}</p>
            <p className="text-label-sm text-on-surface-variant">
              {model.provider} · {MODE_LABELS[model.mode] ?? model.mode}
            </p>
          </div>
        </div>

        {input || output || model.inputCostPerSecond ? (
          <dl className="flex flex-wrap gap-x-6 gap-y-1 text-label-md">
            {input ? <PriceItem label="Input" value={input} /> : null}
            {output ? <PriceItem label="Output" value={output} /> : null}
            {model.inputCostPerSecond ? (
              <PriceItem label="Per detik" value={`$${model.inputCostPerSecond}`} />
            ) : null}
          </dl>
        ) : null}

        {model.maxInputTokens ? (
          <p className="text-label-sm text-on-surface-variant">
            Konteks {model.maxInputTokens.toLocaleString('id-ID')} token
            {model.maxOutputTokens
              ? ` · output ${model.maxOutputTokens.toLocaleString('id-ID')}`
              : ''}
          </p>
        ) : null}

        {capabilities.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {capabilities.map(([capability]) => (
              <Badge key={capability} tone="primary">
                {CAPABILITY_LABELS[capability] ?? capability}
              </Badge>
            ))}
          </div>
        ) : null}
      </CardBody>
    </Card>
  )
}

function PriceItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-label-sm text-on-surface-variant">{label}</dt>
      <dd className="font-medium text-on-surface">{value}</dd>
    </div>
  )
}

/**
 * Forces the catalogue to be read from LiteLLM again.
 *
 * The list is cached for five minutes on the server and another five in the
 * browser, which is fine day to day and maddening right after a model is added
 * or removed. Both halves are cleared here: the endpoint drops the server's
 * copy, and invalidating the query drops this tab's.
 */
function RefreshButton() {
  const queryClient = useQueryClient()

  const refresh = useMutation({
    mutationFn: refreshModels,
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['models'] })
      toast.success(`Daftar model dimuat ulang — ${result.count} model.`)
    },
    onError: () => toast.error('Gagal memuat ulang daftar model'),
  })

  return (
    <Button
      variant="outlined"
      loading={refresh.isPending}
      onClick={() => refresh.mutate()}
      icon={<RefreshCw className="size-4" />}
    >
      Muat ulang
    </Button>
  )
}
